'use client';

import { useQuery } from '@tanstack/react-query';
import axiosInstance from '@libs/axios';
import { usePPJKContext } from '@contexts/PPJKContext';

interface UsageDashboardParams {
  ppjkName?: string;
  /** A super admin's picked PPJK; the server ignores it for everyone else. */
  ppjkNpwp?: string;
  startDate?: string;
  endDate?: string;
  offset?: number;
  limit?: number;
  orderBy?: string;
  ascending?: boolean;
}

interface UsageDashboardItem {
  noAju: string;
  ppjkName: string;
  tanggalDibuat: string;
  namaPerusahaan: string;
  userName: string;
  totalBarang: number;
}

interface UsageDashboardResponse {
  success: boolean;
  message: string;
  totalCount: number;
  data: {
    data: UsageDashboardItem[];
  };
}

interface UsageDashboardResult {
  data: UsageDashboardItem[];
  totalCount: number;
}

async function fetchUsageDashboard({
  ppjkName,
  ppjkNpwp,
  startDate,
  endDate,
  offset = 0,
  limit = 10,
  orderBy,
  ascending,
  signal,
}: UsageDashboardParams & { signal?: AbortSignal }): Promise<UsageDashboardResult> {
  // Check for access token
  if (!window.localStorage.getItem('accessToken') && !document.cookie.includes('accessToken')) {
    console.warn('No access token available, skipping dashboard API call');
    return { data: [], totalCount: 0 };
  }

  const queryParams = new URLSearchParams();
  if (offset !== undefined) queryParams.append('offset', offset.toString());
  if (limit !== undefined) queryParams.append('limit', limit.toString());
  if (orderBy) queryParams.append('orderBy', orderBy);
  if (ascending !== undefined) queryParams.append('ascending', ascending.toString());

  const url = `/api/v1/dashboard/usage-dashboard${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

  const { data: response } = await axiosInstance.post<UsageDashboardResponse>(
    url,
    {
      ppjkName,
      ppjkNpwp,
      startDate,
      endDate,
    },
    { signal }
  );

  return {
    data: response.data?.data ?? [],
    totalCount: response.totalCount ?? 0,
  };
}

export default function useGetUsageDashboard(params?: UsageDashboardParams) {
  const { getPpjkWithFallback } = usePPJKContext();

  const ppjkName = params?.ppjkName || getPpjkWithFallback() || undefined;

  const queryResult = useQuery({
    queryKey: [
      'usage-dashboard',
      {
        ppjkName,
        ppjkNpwp: params?.ppjkNpwp,
        startDate: params?.startDate,
        endDate: params?.endDate,
        offset: params?.offset,
        limit: params?.limit,
        orderBy: params?.orderBy,
        ascending: params?.ascending,
      },
    ],
    queryFn: ({ signal }) =>
      fetchUsageDashboard({
        ...params,
        ppjkName,
        signal,
      }),
    retry: 1,
    staleTime: 30 * 1000, // 30 seconds
  });

  return {
    data: queryResult.data?.data,
    totalCount: queryResult.data?.totalCount ?? 0,
    loading: queryResult.isLoading || queryResult.isFetching,
    error: queryResult.error?.message ?? '',
  };
}

export type { UsageDashboardItem, UsageDashboardParams };
